<template>
  <div v-show="$store.getters.getIsConsideringDoseageAdjustment && !$store.getters.getDisableDoseageAdjustment" class="doseage-container">
      <label class="sub-section-label">
          List the GeneSight medications that you are considering for <b>dosage adjustment</b>
      </label>
      <error-wrapper
          :error-exists="$store.getters.doseageMedicationsError"
          error-message="Please provide a response."
      >
          <div class="doseage-multiselect-container">
              <div class="doseage-multiselect" :class="[isSingleGeneTest && isMedicare ? 'multiselect-single-gene-warning': '', (isSingleGeneTest && $store.getters.getDoseageHighlight) ? 'multiselect-add-medication' : '']" />
          </div>
      </error-wrapper>
  </div>
</template>

<script>
import { NeuroMultiselect } from "@myriad-neuroscience/myriad-neuro-multiselect/neuro-multiselect.js";
import ErrorWrapper from "../../ErrorWrapper.vue";
import Common from "../../../../scripts/common.js";
import Enums from "../../../../scripts/enums.js";

export default {
    name: "Doseage",
    components: {
        ErrorWrapper
    },
    data() {
        return {
            nonGeneticFactors: false,
            nonGeneticFactorsDirty: false,
            augmentHeaderString: "You selected that this patient has Medicare. Medicare coverage for PGx tests is limited to when providers are considering use of one or more medications with a gene-drug interaction that is clinically actionable and designated by the FDA or CPIC (category A or B).",
            singleGeneName: "",
            augmentationObjectList: [],
            doseageObjectList: []
        };
    },
    computed: {
        isMedicare() {
            return this.$store.getters.getCurrentOrderIsMedicare;
        },
        augmentationGene: {
            get() {
                return this.$store.getters.getAugmentationGene;
            },
            set(value) {
                this.$store.dispatch("setAugmentationGene", value);
            }
        },
        doseageGene: {
            get() {
                return this.$store.getters.getDoseageGene;
            },
            set(value) {
                this.$store.dispatch("setDoseageGene", value);
            }
        },
        augmentationMedications: {
            get() {
                return this.$store.getters.getAugmentationMedications;
            }
        },
        doseageMedications: {
            get() {
                return this.$store.getters.getDoseageMedications;
            },
            set(value) {
                this.$store.dispatch("setDoseageMedications", value);
            }
        },
        isSingleGeneTest() {
            const { doseageMedsValid, augMedsValid } = this.getMedInputsValid();
            return doseageMedsValid && augMedsValid && Common.getTestType() === Enums.TestTypes.SINGLE_GENE;
        },
        isZeroGeneTest() {
            const { doseageMedsValid, augMedsValid } = this.getMedInputsValid();
            return doseageMedsValid && augMedsValid && Common.getTestType() === Enums.TestTypes.ZERO_GENE;
        },
        isMultiGeneTest() {
            const { doseageMedsValid, augMedsValid } = this.getMedInputsValid();
            return doseageMedsValid && augMedsValid && Common.getTestType() === Enums.TestTypes.MULTI_GENE;
        },
        combinedMeds() {
            return this.$store.getters.getCombinedMeds;
        },
        otherMeds() {
            return this.$store.getters.getOtherMeds;
        },
        cpicMeds() {
            return this.$store.getters.getCpicMeds;
        },
        cpicOtherMeds() {
            return [
                { "FDA & CPIC A/B Meds": this.cpicMeds },
                { "Other Meds": this.otherMeds }
            ];
        },
        medicationList() {
            var meds = [];

            if (this.$store.getters.getIsConsideringAugmentingWithNewMedication) {
                meds = [...meds, ...this.augmentationList];
            }
            if (this.$store.getters.getIsConsideringDoseageAdjustment && !this.$store.getters.getDisableDoseageAdjustment) {
                meds = [...meds, ...this.doseageList];
            }
            return meds;
        },
        singleGene() {
            if (this.$store.getters.getIsConsideringAugmentingWithNewMedication) {
                return this.augmentationGene;
            }
            if (this.$store.getters.getIsConsideringDoseageAdjustment && !this.$store.getters.getDisableDoseageAdjustment) {
                return this.doseageGene;
            }

            return "";
        },
        augmentationList: {
            get() {
                return this.$store.getters.getSelectedAugmentationMeds;
            },
            set(value) {
                this.$store.dispatch("setSelectedAugmentationMeds", value);
            }
        },
        doseageList: {
            get() {
                return this.$store.getters.getSelectedDoesageMeds;
            },
            set(value) {
                this.$store.dispatch("setSelectedDoesageMed", value);
            }
        }
    },
    watch: {
        combinedMeds(newValues) {
            this.$nextTick(() => {
                this.createDoseageMultiselect(newValues);
            });
        },
        isSingleGeneTest(newValue) {
            if (!newValue) {
                this.disableHighlights();
            }
            this.setSingleGeneStoreInfo(newValue);
        },
        isZeroGeneTest(newValue) {
            this.$store.dispatch("setIsZeroGeneTest", newValue);
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (this.combinedMeds.length > 0) {
                this.createDoseageMultiselect(this.combinedMeds);
            }
        });
        this.setSingleGeneStoreInfo(this.isSingleGeneTest);
    },
    methods: {
        createDoseageMultiselect(medValues) {
            const domElement = document.querySelector(".doseage-multiselect");
            const failedMeds = this.$store.getters.getFailedMedsMultiselect.state.selectedValues;

            if (domElement === null) {
                return;
            }

            domElement.innerHTML = "";

            let previousDoseageMultiselect = this.$store.getters.getDoseageMultiselect;
            const doseageMultiselect = new NeuroMultiselect();
            if (previousDoseageMultiselect === null || previousDoseageMultiselect === undefined) {
                previousDoseageMultiselect = doseageMultiselect;
            }
            this.setPreviousGeneState(doseageMultiselect, previousDoseageMultiselect.state.previousGeneState);

            if (!this.isMedicare) {
                medValues.forEach((med) => {
                    med.subText = "";
                });
            }
            doseageMultiselect.AvailableOptions = medValues;
            doseageMultiselect.applyMatchingSubTextFromAvailableOptions(failedMeds);

            if (this.isMedicare) {
                doseageMultiselect.init(
                    domElement,
                    failedMeds,
                    this.initDoseageList(failedMeds),
                    {
                        allowCustomInputs: false,
                        placeholderString: "Type or select a medication.",
                        footerString: "Only medication(s) in the Failed Medications section above can be considered for dosage adjustment. The same medication cannot be selected for augmentation and dosage adjustment.",
                        showSubTextOnSelectedItem: true
                    }
                );
            } else {
                var failedMedsStringArray = [];
                failedMeds.forEach((med) => {
                    failedMedsStringArray.push(med.value);
                });
                doseageMultiselect.init(
                    domElement,
                    failedMedsStringArray,
                    this.doseageMedications,
                    {
                        allowCustomInputs: false,
                        placeholderString: "Type or select a medication.",
                        footerString: "Only medication(s) in the Failed Medications section above can be considered for dosage adjustment. The same medication cannot be selected for augmentation and dosage adjustment."
                    }
                );
            }

            this.getDoseageSingleGene(failedMeds, this.doseageMedications);

            const that = this;
            doseageMultiselect.setupCallbackReturn(function(selectedValues) {
                that.$store.dispatch("setDoseageHighlight", false);
                that.$store.dispatch("setAugmentationHighlight", false);
                const stringArray = [];
                var doseageList = [];
                that.doseageGene = "";
                selectedValues.forEach((value) => {
                    stringArray.push(value.value);
                    if (value.subText) {
                        that.doseageGene = value.subText; // this value won't be used unless single gene
                        doseageList.push(value);
                    }
                });

                that.doseageMedications = stringArray;
                that.doseageList = doseageList;

                const augmentationMultiselect = that.$store.getters.getAugmentationMultiselect;
                const copyValues = JSON.parse(JSON.stringify(selectedValues));
                augmentationMultiselect.markValuesAsDisabled(copyValues);
            });

            doseageMultiselect.setupBlurCallback(function() {
                Common.trackUserBehaviorInResponseToGeneWarnings();
            });

            this.$store.dispatch("setDoseageMultiselect", doseageMultiselect);
        },
        setPreviousGeneState(multiselect, previousGeneState) {
            if (previousGeneState != null || previousGeneState !== undefined) {
                multiselect.state.previousGeneState = previousGeneState;
            } else {
                if (this.isMultiGeneTest) {
                    multiselect.state.previousGeneState = Enums.TestTypes.MULTI_GENE;
                } else if (this.isSingleGeneTest) {
                    multiselect.state.previousGeneState = Enums.TestTypes.SINGLE_GENE;
                } else if (this.isZeroGeneTest) {
                    multiselect.state.previousGeneState = Enums.TestTypes.ZERO_GENE;
                }
            }
        },
        getMedInputsValid() {
            const doseageMedsSelected = this.$store.getters.getIsConsideringDoseageAdjustment;
            const doseageMedsAnswered = this.doseageMedications.length > 0;

            const augMedsSelected = this.$store.getters.getIsConsideringAugmentingWithNewMedication;
            const augMedsAnswered = this.augmentationMedications.length > 0;

            const doseageMedsValid = doseageMedsSelected ? doseageMedsAnswered : true;
            const augMedsValid = augMedsSelected ? augMedsAnswered : true;

            return {
                doseageMedsValid: doseageMedsValid,
                augMedsValid: augMedsValid
            };
        },
        setSingleGeneStoreInfo(newValue) {
            // we only show single gene if medicare
            if (newValue && this.isMedicare) {
                const testTitle = this.singleGeneName + " Single Gene Report";
                this.$store.dispatch("setTestTitle", testTitle);
            } else {
                this.$store.dispatch("resetTestTitle");
            }
            this.$store.dispatch("setIsSingleGeneTest", newValue && this.isMedicare);
        },
        getDoseageSingleGene(allMeds, medList) {
            if ((allMeds || []).length === 0 || (medList || []).length === 0) {
                return "";
            }
            var keyNames = Object.keys(allMeds[0]);
            if (keyNames.find((prop) => { return prop === 'subText'; })) {
                allMeds.forEach((med) => {
                    if (med.value === medList[0]) {
                        this.doseageGene = med.subText; // this value won't be used unless single gene
                    }
                });
            }
        },
        initDoseageList(failedMeds) {
            var allMeds = [];
            var doseageList = [];
            this.doseageMedications.forEach((m1) => {
                failedMeds.forEach((m2) => {
                    if (m1 === m2.value) {
                        allMeds.push(m2);
                        if (m2.subText) {
                            doseageList.push(m2);
                        }
                    }
                });
            });
            this.doseageList = doseageList;
            return allMeds;
        },
        disableHighlights(show) {
            this.$store.dispatch("setAugmentationHighlight", false);
            this.$store.dispatch("setDoseageHighlight", false);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@myriad-neuroscience/myriad-neuro-multiselect/neuro-multiselect.css";
@import "../../../../styles/AuthorizeOrders.scss";

.doseage-container,
.augment-container
{
  margin-bottom: 35px;
  margin-left: 28px;
  margin-top: 6px;
}

.non-genetic-warning {
  margin-top: 30px;
}

.multiselect-single-gene-warning >>> .myriad-neuro-selected-input-container {
  border: 2px solid #ff6b0b !important;
}

.multiselect-add-medication >>> .myriad-neuro-selected-input-container,
.multiselect-add-medication >>> input[type=text].myriad-neuro-multiselect-input {
  background-color: #FFF0E7 !important;
}
</style>
